import { Fragment, useEffect, useRef, useState } from "react"
import * as Sentry from "@sentry/react"
import { BarcodeScannerWrapper } from "./styled"
import BarcodeScanning from "./BarcodeScanning"
import { useAppSelector } from "app/hooks"
import ScanResult from "./ScanResult"
import SearchFood from "./SearchFoodSection"
import FullPageLoading from "components/Layouts/FullPageLoading"
import { checkHasScanBarcodeScanningEntitlement } from "utils"
import { useUserPlan } from "features/payment/hooks"
import PATHS from "router/paths"
import MyFoodDetailsWithSchedule from "components/foodDetails/MyFoodDetailsWithSchedule"
import {
  isEdamaFood,
  isMyFood,
  isOpenFoodFactFood,
} from "model/food/utils/typeGuards/foodTypeGuards"
import EdamaFoodDetailsWithSchedule from "components/foodDetails/EdamaFoodDetailsWithSchedule"
import { SearchFoodResultType } from "model/food/types"
import OpenFoodFactFoodDetailsWithSchedule from "components/foodDetails/OpenFoodFactFoodDetailsWithSchedule"
import {
  enumerateLabeledVideoInputs,
  probeCameraCapabilities,
  selectBestBackCamera,
} from "utils/selectBackCamera"
import { isIOSWebView } from "utils/webViewDetection"
import {
  BASE_VIDEO_CONSTRAINTS,
  DEFAULT_CAMERA_WIDTH,
  DEFAULT_CAMERA_HEIGHT,
  getDeviceIdFromConstraints,
  isDeviceAvailabilityError,
} from "utils/cameraConstraints"
import { useNavigate } from "react-router-dom"

enum IDisplaySection {
  SCAN_BARCODE,
  SCAN_RESULT,
  SEARCH,
  SEARCHED_FOOD_DETAILS,
}

const BarcodeScanner = () => {
  const { userPlan } = useUserPlan()
  const entitlements = userPlan?.entitlements

  const [checkingBarcodeEntitlement, setCheckingBarcodeEntitlement] = useState(
    () => {
      const hasBarcodeScanningEntitlement =
        checkHasScanBarcodeScanningEntitlement(entitlements ?? [])

      return !hasBarcodeScanningEntitlement
    },
  )

  const navigate = useNavigate()

  const backToDiary = () => {
    navigate(PATHS.app.root)
  }

  useEffect(() => {
    if (entitlements) {
      const hasBarcodeScanningEntitlement =
        checkHasScanBarcodeScanningEntitlement(entitlements)

      if (hasBarcodeScanningEntitlement) {
        setCheckingBarcodeEntitlement(false)
      } else {
        backToDiary()
      }
    }
  }, [entitlements])

  const [displaySection, setDisplaySection] = useState<IDisplaySection>(
    IDisplaySection.SCAN_BARCODE,
  )

  const [foodDetails, setFoodDetails] = useState<SearchFoodResultType>()

  const scanResult = useAppSelector(
    (state) => state.meal.searchFoodByBarcodeResult,
  )

  const onBackFromSearch = () => {
    const nextSection = scanResult
      ? IDisplaySection.SCAN_RESULT
      : IDisplaySection.SCAN_BARCODE

    setDisplaySection(nextSection)
  }

  const [videoConstraints, setVideoConstraints] =
    useState<MediaTrackConstraints | null>(null)

  // Camera selection only needs to run once per BarcodeScanner mount — the
  // selected device stays valid across SCAN_RESULT/SEARCH round trips back
  // to SCAN_BARCODE, so re-enumerating/re-probing devices on every return is
  // redundant (onCameraError already handles a pinned device going stale).
  const hasSelectedCameraRef = useRef(false)

  // Runs in parallel with the entitlement check above (not gated on it) so
  // camera selection is already in flight — often already resolved — by the
  // time checkingBarcodeEntitlement clears and BarcodeScanning actually
  // renders, instead of waterfalling one loading phase after the other.
  useEffect(() => {
    if (displaySection !== IDisplaySection.SCAN_BARCODE) return
    if (hasSelectedCameraRef.current) return

    hasSelectedCameraRef.current = true
    let cancelled = false
    setVideoConstraints(null)

    const selectCamera = async () => {
      try {
        const enumeratedDevices = await enumerateLabeledVideoInputs()
        if (cancelled) return

        let backCameraDeviceId: string | null = null
        if (!isIOSWebView()) {
          backCameraDeviceId = await selectBestBackCamera(
            enumeratedDevices,
            probeCameraCapabilities,
          )
        }
        if (cancelled) return

        setVideoConstraints(
          backCameraDeviceId
            ? {
                deviceId: { exact: backCameraDeviceId },
                width: { ideal: DEFAULT_CAMERA_WIDTH },
                height: { ideal: DEFAULT_CAMERA_HEIGHT },
              }
            : BASE_VIDEO_CONSTRAINTS,
        )
      } catch {
        if (!cancelled) {
          setVideoConstraints(BASE_VIDEO_CONSTRAINTS)
        }
      }
    }

    selectCamera()

    return () => {
      cancelled = true
    }
  }, [displaySection])

  const handleCameraError = (error: string | DOMException) => {
    const deviceId = videoConstraints
      ? getDeviceIdFromConstraints(videoConstraints)
      : null

    if (!deviceId || !isDeviceAvailabilityError(error)) return

    Sentry.withScope((scope) => {
      scope.setFingerprint(["BarcodeScanner", "camera-error"])
      scope.setContext("camera_error", {
        error: { name: error.name, message: error.message },
        deviceId,
      })
      Sentry.captureMessage(
        "[BarcodeScanner/camera-error] pinned camera device failed to open",
        "warning",
      )
    })

    setVideoConstraints(BASE_VIDEO_CONSTRAINTS)
  }

  // Android only serves one camera stream at a time: if the user opens the
  // OS "take photo" sheet while this screen stays mounted, the live barcode
  // stream is preempted and comes back frozen. Bumping this key remounts
  // just the camera element on resume, forcing a fresh getUserMedia request.
  const [cameraResetKey, setCameraResetKey] = useState(0)

  useEffect(() => {
    if (isIOSWebView()) return

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        displaySection === IDisplaySection.SCAN_BARCODE
      ) {
        setCameraResetKey((key) => key + 1)
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [displaySection])

  if (checkingBarcodeEntitlement) {
    return <FullPageLoading />
  }

  if (displaySection === IDisplaySection.SEARCHED_FOOD_DETAILS && foodDetails) {
    return (
      <Fragment>
        {isEdamaFood(foodDetails) && (
          <EdamaFoodDetailsWithSchedule
            food={foodDetails}
            onBack={() => setDisplaySection(IDisplaySection.SEARCH)}
            foodCreationMethod="BARCODE_SCAN"
          />
        )}

        {isOpenFoodFactFood(foodDetails) && (
          <OpenFoodFactFoodDetailsWithSchedule
            food={foodDetails}
            onBack={() => setDisplaySection(IDisplaySection.SEARCH)}
            foodCreationMethod="BARCODE_SCAN"
          />
        )}

        {isMyFood(foodDetails) && (
          <MyFoodDetailsWithSchedule
            food={foodDetails}
            onBack={() => setDisplaySection(IDisplaySection.SEARCH)}
            foodCreationMethod="BARCODE_SCAN"
          />
        )}
      </Fragment>
    )
  }

  return (
    <BarcodeScannerWrapper>
      {displaySection === IDisplaySection.SCAN_BARCODE && (
        <BarcodeScanning
          videoConstraints={videoConstraints}
          cameraResetKey={cameraResetKey}
          onCameraError={handleCameraError}
          onClose={backToDiary}
          goToScanResult={() => setDisplaySection(IDisplaySection.SCAN_RESULT)}
          goToSearch={() => setDisplaySection(IDisplaySection.SEARCH)}
        />
      )}

      {displaySection === IDisplaySection.SCAN_RESULT && scanResult && (
        <ScanResult
          food={scanResult}
          onBack={() => setDisplaySection(IDisplaySection.SCAN_BARCODE)}
          onSearch={() => setDisplaySection(IDisplaySection.SEARCH)}
        />
      )}

      {displaySection === IDisplaySection.SEARCH && (
        <SearchFood
          onBack={onBackFromSearch}
          onSelectFood={(food) => {
            setFoodDetails(food)
            setDisplaySection(IDisplaySection.SEARCHED_FOOD_DETAILS)
          }}
        />
      )}
    </BarcodeScannerWrapper>
  )
}

export default BarcodeScanner
